import React, { useEffect, useRef, useState } from "react";

const CroppedImageFromFile = ({
  imageFile,
  areaData,
  position,
  radius,
  isPublicView,
  isLayout,
  isFloorMAp,
}) => {
  const [isInView, setIsInView] = useState(false);
  const [imageObj, setImageObj] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [snapshotUrl, setSnapshotUrl] = useState(null);
  const [isSnapshotReady, setIsSnapshotReady] = useState(false);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Load the image with CORS support
  useEffect(() => {
    if (!imageFile) {
      console.error("Invalid imageFile:", imageFile);
      return;
    }

    setIsLoaded(false);
    setSnapshotUrl(null);
    setIsSnapshotReady(false);

    const img = new window.Image();

    // CRITICAL: Set crossOrigin BEFORE setting src
    // This prevents canvas tainting
    img.crossOrigin = "anonymous";

    img.onload = () => {
      console.log("Image loaded:", img.width, "x", img.height);
      setImageObj(img);
      setIsLoaded(true);
    };

    img.onerror = (e) => {
      console.error("Error loading image:", e);
      // Try without crossOrigin as fallback
      const fallbackImg = new window.Image();
      fallbackImg.onload = () => {
        console.log(
          "Image loaded (fallback):",
          fallbackImg.width,
          "x",
          fallbackImg.height,
        );
        setImageObj(fallbackImg);
        setIsLoaded(true);
      };
      fallbackImg.onerror = (err) => {
        console.error("Fallback also failed:", err);
      };

      const url =
        imageFile instanceof File ? URL.createObjectURL(imageFile) : imageFile;
      fallbackImg.src = url;
    };

    const url =
      imageFile instanceof File ? URL.createObjectURL(imageFile) : imageFile;
    img.src = url;

    return () => {
      if (imageFile instanceof File) {
        URL.revokeObjectURL(url);
      }
    };
  }, [imageFile]);

  // Update container size on resize
  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setContainerSize({ width, height });
      }
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  // Draw the cropped image on the canvas
  useEffect(() => {
    if (!isLoaded || !imageObj || !canvasRef.current || !containerRef.current)
      return;

    if (!areaData || !position || !radius) {
      console.error("Missing required props:", { areaData, position, radius });
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    const containerWidth =
      containerSize.width || containerRef.current.clientWidth;
    const containerHeight =
      containerSize.height || containerRef.current.clientHeight;

    const circleSize = Math.min(containerWidth, containerHeight);
    const pixelRatio = window.devicePixelRatio || 1;

    canvas.width = circleSize * pixelRatio;
    canvas.height = circleSize * pixelRatio;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(pixelRatio, pixelRatio);

    canvas.style.width = `${circleSize}px`;
    canvas.style.height = `${circleSize}px`;

    ctx.clearRect(0, 0, canvas.width / pixelRatio, canvas.height / pixelRatio);

    const canvasRadius = circleSize / 2;
    ctx.beginPath();
    ctx.arc(canvasRadius, canvasRadius, canvasRadius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    const centerX = isLayout ? (isLayout * imageObj?.width) / 3 : position.x;
    const centerY = isLayout ? (isLayout * imageObj?.height) / 3 : position.y;

    try {
      const sourceX =
        centerX -
        (isLayout ? Math.min(imageObj?.width, imageObj?.height) / 4 : radius);
      const sourceY =
        centerY -
        (isLayout ? Math.min(imageObj?.width, imageObj?.height) / 4 : radius);
      const sourceDiameter =
        (isLayout ? Math.min(imageObj?.width, imageObj?.height) / 4 : radius) *
        2;

      const leftEdge = Math.max(0, sourceX);
      const topEdge = Math.max(0, sourceY);
      const rightEdge = Math.min(imageObj.width, sourceX + sourceDiameter);
      const bottomEdge = Math.min(imageObj.height, sourceY + sourceDiameter);

      const validSourceWidth = rightEdge - leftEdge;
      const validSourceHeight = bottomEdge - topEdge;

      const outsideLeft = Math.max(0, -sourceX);
      const outsideTop = Math.max(0, -sourceY);

      const destX = (outsideLeft / sourceDiameter) * circleSize;
      const destY = (outsideTop / sourceDiameter) * circleSize;
      const destWidth = (validSourceWidth / sourceDiameter) * circleSize;
      const destHeight = (validSourceHeight / sourceDiameter) * circleSize;

      if (
        validSourceWidth <= 0 ||
        validSourceHeight <= 0 ||
        destWidth <= 0 ||
        destHeight <= 0
      ) {
        console.error("Invalid dimensions for drawing");
        return;
      }

      ctx.drawImage(
        imageObj,
        leftEdge,
        topEdge,
        validSourceWidth,
        validSourceHeight,
        destX,
        destY,
        destWidth,
        destHeight,
      );

      ctx.beginPath();
      ctx.arc(canvasRadius, canvasRadius, canvasRadius - 1, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(255, 255, 255, 0.7)";
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.stroke();

      // Convert canvas to data URL
      requestAnimationFrame(() => {
        try {
          // Try different methods to avoid CORS issues
          let dataUrl = null;

          try {
            // Method 1: Standard toDataURL
            dataUrl = canvas.toDataURL("image/png", 1.0);
            console.log(
              "Snapshot generated via toDataURL, length:",
              dataUrl.length,
            );
          } catch (corsError) {
            console.warn(
              "toDataURL failed due to CORS, trying toBlob...",
              corsError.message,
            );

            // Method 2: Use toBlob as fallback
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    dataUrl = reader.result;
                    console.log(
                      "Snapshot generated via toBlob, length:",
                      dataUrl.length,
                    );
                    setSnapshotUrl(dataUrl);
                    setIsSnapshotReady(true);

                    if (containerRef.current) {
                      containerRef.current.setAttribute(
                        "data-crop-ready",
                        "true",
                      );
                      containerRef.current.setAttribute(
                        "data-snapshot-ready",
                        "true",
                      );
                    }
                  };
                  reader.readAsDataURL(blob);
                } else {
                  console.error("toBlob returned null");
                  // Fall back to showing canvas directly
                  if (containerRef.current) {
                    containerRef.current.setAttribute(
                      "data-crop-ready",
                      "true",
                    );
                    containerRef.current.setAttribute(
                      "data-canvas-only",
                      "true",
                    );
                  }
                }
              },
              "image/png",
              1.0,
            );
            return; // Exit early since toBlob is async
          }

          if (dataUrl) {
            setSnapshotUrl(dataUrl);
            setIsSnapshotReady(true);

            if (containerRef.current) {
              containerRef.current.setAttribute("data-crop-ready", "true");
              containerRef.current.setAttribute("data-snapshot-ready", "true");
            }
          }
        } catch (err) {
          console.error("Error generating snapshot dataUrl", err);
          console.error("Error details:", {
            name: err.name,
            message: err.message,
            stack: err.stack,
          });

          // Mark as ready anyway so PDF generation doesn't hang
          // It will just use the canvas element directly
          if (containerRef.current) {
            containerRef.current.setAttribute("data-crop-ready", "true");
            containerRef.current.setAttribute("data-canvas-only", "true");
          }
        }
      });
    } catch (error) {
      console.error("Error drawing image on canvas:", error);
    }
  }, [imageObj, isLoaded, areaData, position, radius, containerSize, isInView]);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.1 },
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (!isLoaded || !imageObj) {
    return (
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: "50%",
            height: "50%",
            borderRadius: "50%",
            background: "#f0f0f0",
            animation: "pulse 1.5s infinite ease-in-out",
          }}
        />
        <style>{`
          @keyframes pulse {
            0% { opacity: 0.3; }
            50% { opacity: 1; }
            100% { opacity: 0.3; }
          }
        `}</style>
      </div>
    );
  }

  if (!areaData || !position || !radius) {
    console.error("Missing required props:", { areaData, position, radius });
    return (
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          overflow: "hidden",
        }}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        overflow: "hidden",
      }}
      data-crop-container="true"
    >
      {/* Render snapshot image if available */}
      {snapshotUrl && (
        <img
          src={snapshotUrl}
          alt="Cropped preview"
          className="cropped-snapshot"
          data-snapshot-image="true"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "50%",
            display: "block",
          }}
          onLoad={() => {
            console.log("Snapshot image loaded in DOM");
            if (containerRef.current) {
              containerRef.current.setAttribute("data-image-loaded", "true");
            }
          }}
          onError={(e) => {
            console.error("Error loading snapshot image:", e);
          }}
        />
      )}

      {/* Always keep canvas as fallback */}
      <canvas
        ref={canvasRef}
        data-canvas-element="true"
        style={{
          width: "100%",
          height: "100%",
          display: snapshotUrl ? "none" : "block",
          position: snapshotUrl ? "absolute" : "relative",
          inset: 0,
        }}
      />
    </div>
  );
};

export default CroppedImageFromFile;
